/**
 * EncounterSlice
 * 
 * Redux slice for managing encounter state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, EncounterSliceState } from './state';
import { buildEncounterExtraReducers } from './extraReducers';

// Import all reducers
import {
  setEncounter,
  clearEncounter,
  editEncounter,
  addProcedure,
  updateProcedure,
  removeProcedure,
  setProcedures,
  moveProcedureUp,
  moveProcedureDown,
  addInsurance,
  updateInsurance,
  removeInsurance,
  setInsurances,
  moveInsuranceUp,
  moveInsuranceDown,
  addScheduledProcedureCode,
  updateScheduledProcedureCode,
  removeScheduledProcedureCode,
  setScheduledProcedureCodes,
  moveScheduledProcedureCodeUp,
  moveScheduledProcedureCodeDown,
  markFieldAsEdited,
  clearEditedFields,
  markAsSaved,
  discardChanges,
  setValidationError,
  clearValidationError,
  clearAllValidationErrors,
} from './reducers';

/**
 * Encounter slice definition
 */
const encounterSlice = createSlice({
  name: 'encounter',
  initialState,
  reducers: {
    // Core reducers
    setEncounter,
    clearEncounter,
    editEncounter,

    // Procedures reducers
    addProcedure,
    updateProcedure,
    removeProcedure,
    setProcedures,
    moveProcedureUp,
    moveProcedureDown,

    // Insurances reducers
    addInsurance,
    updateInsurance,
    removeInsurance,
    setInsurances,
    moveInsuranceUp,
    moveInsuranceDown,

    // Scheduled procedure codes reducers
    addScheduledProcedureCode,
    updateScheduledProcedureCode,
    removeScheduledProcedureCode,
    setScheduledProcedureCodes,
    moveScheduledProcedureCodeUp,
    moveScheduledProcedureCodeDown,

    // Edit tracking reducers
    markFieldAsEdited,
    clearEditedFields,
    markAsSaved,
    discardChanges,

    // Validation reducers
    setValidationError,
    clearValidationError,
    clearAllValidationErrors,
  },
  extraReducers: buildEncounterExtraReducers,
});

// Export actions
export const encounterActions = encounterSlice.actions;

// Export individual actions for convenience
export const {
  setEncounter: setEncounterAction,
  clearEncounter: clearEncounterAction,
  editEncounter: editEncounterAction,
  addProcedure: addProcedureAction,
  updateProcedure: updateProcedureAction,
  removeProcedure: removeProcedureAction,
  setProcedures: setProceduresAction,
  moveProcedureUp: moveProcedureUpAction,
  moveProcedureDown: moveProcedureDownAction,
  addInsurance: addInsuranceAction,
  updateInsurance: updateInsuranceAction,
  removeInsurance: removeInsuranceAction,
  setInsurances: setInsurancesAction,
  moveInsuranceUp: moveInsuranceUpAction,
  moveInsuranceDown: moveInsuranceDownAction,
  addScheduledProcedureCode: addScheduledProcedureCodeAction,
  updateScheduledProcedureCode: updateScheduledProcedureCodeAction,
  removeScheduledProcedureCode: removeScheduledProcedureCodeAction,
  setScheduledProcedureCodes: setScheduledProcedureCodesAction,
  moveScheduledProcedureCodeUp: moveScheduledProcedureCodeUpAction,
  moveScheduledProcedureCodeDown: moveScheduledProcedureCodeDownAction,
  markFieldAsEdited: markFieldAsEditedAction,
  clearEditedFields: clearEditedFieldsAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = encounterSlice.actions;

// Export reducer
export default encounterSlice.reducer;

// Re-export state types
export type { EncounterSliceState } from './state';

// Re-export selectors
export * from './selectors';
